import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocationStatus from "../components/LocationStatus";
import { Location, getStatusInfo } from "../data/storeHours";
import { trackCallClick, trackOrderInitiation } from "../utils/analytics";
import "../styles/OrderPage.css";

const OrderPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language as "en" | "fr";

  // State for open/closed status (updates every minute)
  const [montrealStatus, setMontrealStatus] = useState(() =>
    getStatusInfo("montreal", currentLang),
  );
  const [delsonStatus, setDelsonStatus] = useState(() =>
    getStatusInfo("delson", currentLang),
  );

  // Update status every minute and when language changes
  useEffect(() => {
    const updateStatuses = () => {
      setMontrealStatus(getStatusInfo("montreal", currentLang));
      setDelsonStatus(getStatusInfo("delson", currentLang));
    };

    // Update immediately when language changes
    updateStatuses();

    // Update every minute
    const interval = setInterval(updateStatuses, 60000);

    return () => clearInterval(interval);
  }, [currentLang]);

  const locationData = {
    montreal: {
      name: currentLang === "en" ? "MONTREAL" : "MONTRÉAL",
      hours: t("locations.montreal.hours"),
      address:
        currentLang === "fr"
          ? "2532 rue Ste-Catherine E\nMontréal, QC H2K 2K2"
          : "2532 St Catherine St E\nMontreal, QC H2K 2K2",
      phone: "514-527-2532",
      phoneLink: "tel:5145272532",
    },
    delson: {
      name: "DELSON",
      hours: t("locations.delson.hours"),
      address: "35 Boulevard Georges-Gagné Sud\nDelson, QC J5B 2E4",
      phone: "450-632-5320",
      phoneLink: "tel:4506325320",
    },
  };

  const steps =
    currentLang === "fr"
      ? [
          {
            number: "1",
            title: "Choisir l'emplacement",
            description:
              "Sélectionnez votre emplacement LA CAVA GRECQUE préféré",
          },
          {
            number: "2",
            title: "Appeler pour commander",
            description:
              "Appelez directement l'emplacement pour passer votre commande",
          },
          {
            number: "3",
            title: "Emporter ou livraison",
            description:
              "Choisissez emporter ou livraison lors de votre commande",
          },
        ]
      : [
          {
            number: "1",
            title: "Choose Location",
            description: "Select your preferred LA CAVA GRECQUE location",
          },
          {
            number: "2",
            title: "Call to Order",
            description: "Call the location directly to place your order",
          },
          {
            number: "3",
            title: "Pick Up or Delivery",
            description: "Choose pickup or delivery when placing your order",
          },
        ];

  return (
    <div className="order-page">
      <section className="order-hero">
        <div className="order-hero-content">
          <h1 className="order-hero-title">
            {currentLang === "fr" ? "COMMANDER MAINTENANT" : "ORDER NOW"}
          </h1>
          <p className="order-hero-description">
            {currentLang === "fr"
              ? "Choisissez votre emplacement pour passer une commande pour emporter ou livraison"
              : "Choose your location to place an order for pickup or delivery"}
          </p>
        </div>
      </section>

      <section className="order-locations">
        <div className="container">
          <div className="order-locations-grid">
            {/* Montreal Location */}
            <div className="order-location-card">
              <h2 className="order-location-title">
                {locationData.montreal.name}
              </h2>
              <LocationStatus
                location={"montreal" as Location}
                lang={currentLang}
                hours={locationData.montreal.hours}
                classPrefix="order-location"
              />
              <div className="order-location-address">
                {locationData.montreal.address
                  .split("\n")
                  .map((line, index) => (
                    <React.Fragment key={index}>
                      {line}
                      {index <
                        locationData.montreal.address.split("\n").length -
                          1 && <br />}
                    </React.Fragment>
                  ))}
              </div>
              <a
                href={locationData.montreal.phoneLink}
                className="order-location-phone"
              >
                {locationData.montreal.phone}
              </a>
              <div className="order-location-actions">
                {montrealStatus.isOpen ? (
                  <a
                    href={locationData.montreal.phoneLink}
                    className="order-btn order-btn-primary"
                    onClick={() => {
                      trackCallClick("montreal", locationData.montreal.phone);
                      trackOrderInitiation("montreal");
                    }}
                  >
                    {currentLang === "fr"
                      ? "APPELER POUR COMMANDER"
                      : "CALL TO ORDER"}
                  </a>
                ) : (
                  <button
                    className="order-btn order-btn-primary order-btn-disabled"
                    disabled
                  >
                    {currentLang === "fr"
                      ? "APPELER POUR COMMANDER"
                      : "CALL TO ORDER"}
                  </button>
                )}
                <Link to="/montreal" className="order-btn order-btn-secondary">
                  {currentLang === "fr"
                    ? "VOIR L'EMPLACEMENT"
                    : "VIEW LOCATION"}
                </Link>
              </div>
            </div>

            {/* Delson Location */}
            <div className="order-location-card">
              <h2 className="order-location-title">
                {locationData.delson.name}
              </h2>
              <LocationStatus
                location={"delson" as Location}
                lang={currentLang}
                hours={locationData.delson.hours}
                classPrefix="order-location"
              />
              <div className="order-location-address">
                {locationData.delson.address.split("\n").map((line, index) => (
                  <React.Fragment key={index}>
                    {line}
                    {index <
                      locationData.delson.address.split("\n").length - 1 && (
                      <br />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <a
                href={locationData.delson.phoneLink}
                className="order-location-phone"
              >
                {locationData.delson.phone}
              </a>
              <div className="order-location-actions">
                {delsonStatus.isOpen ? (
                  <a
                    href={locationData.delson.phoneLink}
                    className="order-btn order-btn-primary"
                    onClick={() => {
                      trackCallClick("delson", locationData.delson.phone);
                      trackOrderInitiation("delson");
                    }}
                  >
                    {currentLang === "fr"
                      ? "APPELER POUR COMMANDER"
                      : "CALL TO ORDER"}
                  </a>
                ) : (
                  <button
                    className="order-btn order-btn-primary order-btn-disabled"
                    disabled
                  >
                    {currentLang === "fr"
                      ? "APPELER POUR COMMANDER"
                      : "CALL TO ORDER"}
                  </button>
                )}
                <Link to="/delson" className="order-btn order-btn-secondary">
                  {currentLang === "fr"
                    ? "VOIR L'EMPLACEMENT"
                    : "VIEW LOCATION"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="order-info">
        <div className="container">
          <div className="order-info-content">
            <h2 className="order-info-title">
              {currentLang === "fr" ? "COMMENT COMMANDER" : "HOW TO ORDER"}
            </h2>
            <div className="order-info-steps">
              {steps.map((step) => (
                <div key={step.number} className="order-step">
                  <div className="order-step-number">{step.number}</div>
                  <h3 className="order-step-title">{step.title}</h3>
                  <p className="order-step-description">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrderPage;
